import { NavLink } from 'react-router';
import { Clapperboard, PRIMARY_NAV, SECONDARY_NAV, type NavDestination } from './navigation';
import { cn } from '@/lib/cn';

/**
 * The persistent left rail.
 *
 * Kept narrow and quiet. It is furniture, not content, and on a screen this
 * size every pixel it takes is a pixel the library does not get.
 */
export function Sidebar() {
  return (
    <nav
      aria-label="Main"
      className="flex h-full w-[15rem] shrink-0 flex-col gap-1 border-r border-edge bg-bg-sunken/60 px-3 py-4"
    >
      <div className="mb-5 flex items-center gap-2.5 px-3">
        <span
          aria-hidden="true"
          className="flex size-8 items-center justify-center rounded-[var(--radius-md)] bg-accent text-text-on-accent shadow-2"
        >
          <Clapperboard size={17} strokeWidth={2.2} />
        </span>
        <span className="text-[0.9375rem] font-semibold tracking-tight text-text-1">CineSync</span>
      </div>

      <NavGroup items={PRIMARY_NAV} />

      <div className="flex-1" />

      <NavGroup items={SECONDARY_NAV} />
    </nav>
  );
}

function NavGroup({ items }: { readonly items: readonly NavDestination[] }) {
  return (
    <ul className="flex flex-col gap-0.5">
      {items.map((item) => (
        <li key={item.to}>
          <NavItem item={item} />
        </li>
      ))}
    </ul>
  );
}

function NavItem({ item }: { readonly item: NavDestination }) {
  const Icon = item.icon;

  const shared =
    'group relative flex h-9 items-center gap-2.5 rounded-[var(--radius-md)] px-3 text-[0.8125rem] font-medium';

  if (item.status === 'upcoming') {
    return (
      <span
        aria-disabled="true"
        title={`Arrives in ${item.arrivesIn ?? 'a later phase'}`}
        className={cn(shared, 'cursor-default text-text-3/70')}
      >
        <Icon size={16} strokeWidth={2} aria-hidden="true" className="shrink-0" />
        <span className="truncate">{item.label}</span>
        <span className="ml-auto size-1.5 rounded-full bg-text-3/40" aria-hidden="true" />
      </span>
    );
  }

  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        cn(
          shared,
          'transition-[background-color,color] duration-[calc(var(--duration-fast)*var(--motion-scale))]',
          isActive
            ? 'bg-accent-soft text-text-1'
            : 'text-text-2 hover:bg-surface-2 hover:text-text-1',
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* The active marker is a separate element so it can animate
              independently of the label's colour. */}
          <span
            aria-hidden="true"
            className={cn(
              'absolute left-0 h-4 w-[3px] rounded-r-full bg-accent',
              'transition-[opacity,transform] duration-[calc(var(--duration-base)*var(--motion-scale))]',
              isActive ? 'opacity-100' : 'scale-y-0 opacity-0',
            )}
          />
          <Icon size={16} strokeWidth={2} aria-hidden="true" className="shrink-0" />
          <span className="truncate">{item.label}</span>
        </>
      )}
    </NavLink>
  );
}
